import {
  GuidId,
  HashDataId,
  ListPrimaryKeyId,
  PrimaryKeyId,
  ProjectPrimaryKeyId,
} from "./brands.model";
import { ProjectPermission, ProjectUserType } from "./team.model";
import { TenantRole } from "./tenant.model";

/** Project display interface */
export type IProjectView = {
  readonly id: ProjectPrimaryKeyId;
  readonly hashedProjectId: HashDataId;
  readonly projectGuid: GuidId;
  readonly type: number; //0 aparto, 1 multi-sites, 2 land dev.
  readonly name: string;
  readonly reference: string;
  readonly created: string;
  readonly lastProjectSyncDate: number;
  readonly lastModified: number;
  readonly lastModifiedDate: Date;
};

/**
 * Available order types for resetting defects against a project
 */
export enum IProjectDefectOrder {
  DEFAULT = "default",
  CREATE_DATE = "createdate",
}

/**
 *
 */
export enum IProjectType {
  HOUSE = 0,
  MULTIPLE = 1,
  UNITS = 2,
  OFFICE = 3,
}

export namespace Project {
  /** Project interface */
  export type Project = {
    /** id may be -1 when it is a new unsaved project */
    id: ProjectPrimaryKeyId;
    projectGuid: GuidId;
    name: string;
    reference: string;
    address: string;
    city: string;
    postcode: string;
    /** State, i.e. NSW, VIC */
    state: string;
    country: string;
    /** 0 house, 1 multi-sites, 2 land dev. */
    type: IProjectType;
    client: string;
    role: TenantRole;
    photo: ProjectPhoto;
    created: string;
    lastModified: number;
    /** Client portal master toggle */
    clientEnabled: boolean;
    /** Not used */
    // managerId: PrimaryKeyId;
    // status: 0 | 1;
    // tenantId: PrimaryKeyId;
  };

  type ProjectPhoto = {
    url: string;
    photoId: PrimaryKeyId;
  };

  /** Site list interface */
  export type ListView = {
    cloudId: ListPrimaryKeyId;
    hashId: HashDataId;
    defectsCount: number;
    newCount: number;
    inProgressCount: number;
    completeCount: number;
    unit: string;
    address: string;
    floor: string;
    /** @deprecated - Unused at this point  */
    overdueCount: number;
    lastModified: number;
    lastModifiedDefect: number;
    isSynced: 0 | 1 | number;
  };

  export type ListBulkEdits = {
    /** pass `null` to avoid updating values */
    unit: string | null;
    /** pass `null` to avoid updating values */
    address: string | null;
    /** pass `null` to avoid updating values */
    floor: string | null;
  };

  /**
   * Modified Contractor for displaying against project
   * Default model for Project > Selected Assignments
   */
  export type Contractor = {
    /** Project Map Id */
    readonly projectMapId: ProjectPrimaryKeyId | null;
    /** Contractor Cloud Id */
    readonly contractorCloudId: PrimaryKeyId;
    readonly company: string;
    readonly name: string;
    readonly email: string;
    defaultAssignment: boolean;
  };

  /**
   * Relates to server/cloud facing data, all IDs = Cloud
   */
  export type TeamMember = {
    /** DB ref */
    entityId: number | null;
    /** Cloud ID */
    userId: number | null;
    /** Either `firstname + lastname` OR `group name` */
    displayName: string;
    /**
     * For future group descriptions
     * @deprecated - not in use yet
     */
    description: string;
    email: string;
    /** Owner, Editor or `""` */
    permission: ProjectPermission;
    /** User or Group */
    entityType: ProjectUserType;
    /** Group Letter - for tables that group by name */
    groupLabel?: string;
  };

  /**
   * Online Report Details
   * @since 1.5.0
   */
  export type Report = {
    reportId: number;
    type: string;
    size: number;
    mtime: number;
    uri: string;
    filename: string;
    extension: string;
    /** Extension for: `https://reports.defectwise.com.au/download/${shareId}` */
    shareId: string | "";
  };
}

/** Project file interface */
// export interface IProjectFile extends StatResult {
//     filename: string;
//     extension: string;
// }

/** Required data for query to update defect numbers */
export interface IProjectDefectReorder {
  id: number;
}

/**
 * data required by project page to update site
 * list counts and details summary count component
 */
export interface IProjectProgressSummaryUpdate {
  /** main id */
  listId: number;
  /** main id */
  defectId: number;
  /** null if new defect */
  oldStatus: number | null | undefined;
  /** null if defect was deleted */
  newStatus: number | null | undefined;
}

/**
 * Class for managing data related to the defects-status-summary.component
 * Performs some basic logic to assist
 */
export class IDefectStatusSummary {
  newCount: number;
  inProgressCount: number;
  completeCount: number;
  overdueCount: number;
  inProgressPercentage: number = 0;
  completedPercentage: number = 0;
  total: number = 0;

  constructor(
    newCount?: number,
    inProgressCount?: number,
    completeCount?: number,
    overdueCount?: number
  ) {
    this.newCount = newCount ?? 0;
    this.inProgressCount = inProgressCount ?? 0;
    this.completeCount = completeCount ?? 0;
    this.overdueCount = overdueCount ?? 0;
  }

  /** Reset all counts to zero */
  reset() {
    this.newCount = 0;
    this.inProgressCount = 0;
    this.completeCount = 0;
    this.overdueCount = 0;
    this.total = 0;
  }

  /** return total number of counts for all type */
  getTotal(): number {
    return (
      this.newCount +
      this.inProgressCount +
      this.completeCount +
      this.overdueCount
    );
  }

  setPercentages(): void {
    this.total = this.getTotal();
    this.inProgressPercentage =
      this.inProgressCount > 0
        ? Math.round((this.inProgressCount / this.total) * 100)
        : 0;
    this.completedPercentage =
      this.completeCount > 0
        ? Math.round((this.completeCount / this.total) * 100)
        : 0;
  }
}
